import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from '@/lib/query'
import { useUIStore } from '@/stores/uiStore'
import { ErrorBoundary } from '@/components/common'
import App from './App'
import './index.css'

// Initialize i18n (must be imported before App renders)
import '@/i18n'

// Handle legacy hash-based URLs BEFORE React mounts
// This ensures the redirect happens before any auth checks
;(function handleLegacyHashRedirect() {
  const hash = window.location.hash

  // Only process if there's a hash that looks like an old route
  if (!hash || !hash.startsWith('#/')) {
    return
  }

  // Extract the path and query from the hash
  const hashContent = hash.slice(1) // Remove the leading #
  const [hashPath, hashQuery] = hashContent.split('?')
  const queryString = hashQuery ? `?${hashQuery}` : ''

  // Define redirect mappings
  let newPath: string | null = null

  // /#/reset-password -> /reset-password
  if (hashPath === '/reset-password') {
    newPath = `/reset-password${queryString}`
  }
  // /#/activate-account -> /activate
  else if (hashPath === '/activate-account') {
    newPath = `/activate${queryString}`
  }
  // /#/conversations/{id}/show -> /conversations/{id}/overview
  else if (hashPath.match(/^\/conversations\/([^/]+)\/show$/)) {
    const match = hashPath.match(/^\/conversations\/([^/]+)\/show$/)
    if (match) {
      const id = match[1]
      newPath = `/conversations/${id}/overview${queryString}`
    }
  }
  // /#/conversations/redirect/{twilio_id} -> /conversations/redirect/{twilio_id}
  else if (hashPath.match(/^\/conversations\/redirect\/([^/]+)$/)) {
    const match = hashPath.match(/^\/conversations\/redirect\/([^/]+)$/)
    if (match) {
      const twilioId = match[1]
      newPath = `/conversations/redirect/${twilioId}${queryString}`
    }
  }

  if (newPath) {
    // Redirect immediately before React mounts
    window.location.replace(newPath)
  }
})()

// Initialize theme on app load
useUIStore.getState().initTheme()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
)
